import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { LiquidacionesService } from '../../../../services/liquidaciones.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { LoggerService } from '../../../../core/services/logger.service';
import { inject } from '@angular/core';

export interface DocumentosDialogData {
    ordenServicioId: number;
    numeroOrdenServicio: string;
}

export interface DocumentoPdf {
    id: string;
    fileName: string;
    fileType: string;
    comments?: string;
    createdBy: string;
    creationDate: string;
}

export interface ArchivoOrdenServicio {
    id: string;
    nombre: string;
    tipo: string;
    comentarios?: string;
    creadoPor: string;
    fechaCreacion: string;
}

@Component({
    selector: 'app-documentos-orden-servicio-dialog',
    templateUrl: './documentos-orden-servicio-dialog.component.html',
    styleUrls: ['./documentos-orden-servicio-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatTableModule,
        MatChipsModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatCardModule
    ],
    providers: [LoggerService]
})
export class DocumentosOrdenServicioDialogComponent implements OnInit {
    // Formulario para subir archivos
    uploadForm: FormGroup;
    
    // Lista de archivos
    archivos: ArchivoOrdenServicio[] = [];
    
    // Estado de carga
    isLoading = false;
    
    // Tipos de documento disponibles
    tiposDocumento = [
        { id: 'factura', nombre: 'Factura' },
        { id: 'respaldo_cliente', nombre: 'Respaldo Cliente' },
        { id: 'comprobante_pago', nombre: 'Comprobante pago' }
    ];

    private logger = inject(LoggerService);
    private snackBar = inject(MatSnackBar);
    private fb = inject(FormBuilder);
    private liquidacionesService = inject(LiquidacionesService);

    /**
     * Formatea el número de orden de servicio eliminando ceros a la izquierda
     */
    get numeroOrdenServicio(): string {
        return this.data.numeroOrdenServicio.replace(/^0+/, '');
    }

    constructor(
        // eslint-disable-next-line no-unused-vars
        private dialogRef: MatDialogRef<DocumentosOrdenServicioDialogComponent>,
        @Inject(MAT_DIALOG_DATA) 
        // eslint-disable-next-line no-unused-vars
        public data: DocumentosDialogData
    ) {
        // Inicializar formulario
        this.uploadForm = this.fb.group({
            archivo: [null, Validators.required],
            tipo: ['', Validators.required],
            comentarios: ['']
        });
    }

    ngOnInit(): void {
        this.cargarArchivos();
    }

    /**
     * Carga la lista de archivos de la orden de servicio
     */
    /**
     * Formatea una fecha al formato dd-mm-aaaa
     */
    private formatearFecha(fecha: string): string {
        const date = new Date(fecha);
        const dia = date.getDate().toString().padStart(2, '0');
        const mes = (date.getMonth() + 1).toString().padStart(2, '0');
        const anio = date.getFullYear();
        return `${dia}-${mes}-${anio}`;
    }

    /**
     * Extrae el mensaje de error del backend desde diferentes formatos
     * Maneja casos donde el backend responde JSON pero Angular lo recibe como Blob
     */
    private async extraerMensajeError(unknownError: unknown): Promise<string | null> {
        const maybeHttpError: any = unknownError as any;
        if (!maybeHttpError) return null;

        // Intentar leer mensaje desde e.error
        const rawError = maybeHttpError.error ?? maybeHttpError;

        try {
            // Caso: Blob (por responseType: 'blob')
            if (rawError instanceof Blob) {
                const text = await rawError.text();
                try {
                    const parsed = JSON.parse(text);
                    if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
                        return parsed.message;
                    }
                    if (parsed && typeof parsed.error === 'string' && parsed.error.trim()) {
                        return parsed.error;
                    }
                } catch {
                    // Si no se puede parsear JSON, continuar a otras estrategias
                }
            }

            // Caso: objeto con message
            if (rawError && typeof rawError === 'object' && 'message' in rawError && typeof rawError.message === 'string') {
                return rawError.message as string;
            }

            // Caso: string (intentar parsear o retornar directo)
            if (typeof rawError === 'string') {
                try {
                    const parsed = JSON.parse(rawError);
                    if (parsed && typeof parsed.message === 'string') return parsed.message;
                } catch {
                    return rawError;
                }
            }
        } catch {
            // Si todo falla, retornar null y se usará un mensaje genérico
        }

        return null;
    }

    async cargarArchivos(): Promise<void> {
        try {
            this.isLoading = true;
            const response = await firstValueFrom(
                this.liquidacionesService.getDocumentosPdfOrdenServicio(this.data.ordenServicioId)
            );
            
            if (response && response.data) {
                this.archivos = response.data.map((doc: any) => ({
                    id: doc.id || doc.fileName,
                    nombre: doc.fileName,
                    tipo: doc.fileType,
                    comentarios: doc.comments,
                    creadoPor: doc.createdBy,
                    fechaCreacion: this.formatearFecha(doc.creationDate)
                }));
            } else {
                this.archivos = [];
            }
        } catch (error) {
            this.logger.error('Error al cargar archivos:', error as Error);
            this.snackBar.open('Error al cargar los archivos', 'Cerrar', { duration: 5000 });
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Maneja el evento de selección de archivo
     */
    onFileSelected(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.uploadForm.patchValue({
                archivo: file
            });
        }
    }

    /**
     * Sube un nuevo archivo
     */
    async subirArchivo(): Promise<void> {
        if (this.uploadForm.valid) {
            try {
                this.isLoading = true;
                // TODO: Implementar llamada al servicio
                await new Promise(resolve => setTimeout(resolve, 1000)); // Simulación
                this.snackBar.open('Archivo subido exitosamente', 'Cerrar', { duration: 3000 });
                this.uploadForm.reset();
                await this.cargarArchivos();
            } catch (error) {
                this.logger.error('Error al subir archivo:', error as Error);
                this.snackBar.open('Error al subir el archivo', 'Cerrar', { duration: 5000 });
            } finally {
                this.isLoading = false;
            }
        }
    }

    /**
     * Descarga un archivo individual
     */
    async descargarArchivo(archivo: ArchivoOrdenServicio): Promise<void> {
        try {
            this.isLoading = true;
            const response = await firstValueFrom(
                this.liquidacionesService.descargarDocumentoPdf(this.data.ordenServicioId, archivo.nombre)
            );

            // Verificar si el archivo está vacío o no es un PDF
            if (response.size === 0 || response.type !== 'application/pdf') {
                throw new Error('ARCHIVO_NO_ENCONTRADO');
            }

            // Crear un objeto URL para el blob
            const url = window.URL.createObjectURL(response);
            
            // Crear un elemento <a> temporal
            const link = document.createElement('a');
            link.href = url;
            link.download = archivo.nombre; // Usar el nombre original del archivo
            
            // Simular clic para descargar
            document.body.appendChild(link);
            link.click();
            
            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.snackBar.open('Archivo descargado exitosamente', 'Cerrar', { duration: 3000 });
        } catch (error) {
            this.logger.error('Error al descargar archivo:', error as Error);
            if ((error as Error).message === 'ARCHIVO_NO_ENCONTRADO') {
                this.snackBar.open('El archivo solicitado no se encuentra disponible', 'Cerrar', { duration: 5000 });
            } else {
                this.snackBar.open('Error al descargar el archivo', 'Cerrar', { duration: 5000 });
            }
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Descarga todos los documentos disponibles en un solo archivo PDF
     */
    async descargarTodos(): Promise<void> {
        if (this.archivos.length === 0) return;

        try {
            this.isLoading = true;
            const response = await firstValueFrom(
                this.liquidacionesService.descargarTodosDocumentosPdf(this.data.ordenServicioId)
            );

            // Crear un objeto URL para el blob
            const url = window.URL.createObjectURL(response);
            
            // Crear un elemento <a> temporal
            const link = document.createElement('a');
            link.href = url;
            link.download = `documentos_OS_${this.numeroOrdenServicio}.pdf`;
            
            // Simular clic para descargar
            document.body.appendChild(link);
            link.click();
            
            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.snackBar.open('Documentos descargados exitosamente', 'Cerrar', { duration: 3000 });
        } catch (error) {
            this.logger.error('Error al descargar los documentos:', error as Error);
            let errorMessage = 'Error al descargar los documentos';
            const backendMessage = await this.extraerMensajeError(error);
            if (backendMessage) {
                errorMessage = backendMessage;
            }
            this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Cierra el diálogo
     */
    cerrar(): void {
        this.dialogRef.close();
    }
}
